// apps/admin/src/features/legal-entities/TaxConfigSection.tsx
//
// Phase 8.2 Wave 4 Task 23 — Tax Configuration editor section.
//
//  (Tax Configuration Editor Amendment #3) +:
//   - tax_type: NONE | VAT | GST | SALES_TAX
//   - standard_rate: 0–30% (decimal 0–0.30)
//   - registration_number, jurisdiction (ISO-3166 auto-suggested from
//     address country), reverse_charge_eligible (default true for EU B2B),
//     invoice_tax_label
//   - When tax_type === 'NONE' → all sub-fields are disabled (zod schema
//     still passes whatever values are present; backend ignores).
//   - VAT/GST/SALES_TAX → registration + rate required (UI marks them
//     with `*` so the form-level zod validator catches empties).
//
// Test IDs per PLAN/task-23.md:
//   - legal-entity-tax-type-select
//   - legal-entity-tax-rate-input
//   - legal-entity-tax-registration-input
//   - legal-entity-tax-jurisdiction-select
//   - legal-entity-reverse-charge-checkbox

import { useEffect, useMemo } from 'react';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@printway/ui';
import { useTranslation } from '../../i18n/useTranslation.js';

// ---------------------------------------------------------------------------
// Types — mirror LegalEntityForm's `tax_config` shape.
// ---------------------------------------------------------------------------

export type TaxType = 'VAT' | 'GST' | 'SALES_TAX' | 'NONE';

export interface TaxConfigValue {
  type: TaxType;
  /** Fractional 0–0.30 (= 0–30%). API contract stores either decimal or bps
   * — this UI works in decimal then the form-level layer converts as needed. */
  standard_rate: number;
  registration_number: string | null;
  /** ISO-3166-alpha2 + optional subdivision (e.g. HK, VN, US-CA). */
  jurisdiction: string;
  reverse_charge_eligible: boolean;
  invoice_tax_label: string | null;
}

export interface TaxConfigSectionProps {
  value: TaxConfigValue;
  onChange: <K extends keyof TaxConfigValue>(key: K, val: TaxConfigValue[K]) => void;
  /** Country code (ISO-3166-alpha2) parsed from address.country — used to
   * auto-suggest jurisdiction on type change. Pass null/'' if address not set. */
  addressCountry?: string | null;
  /** Field-keyed error map (e.g. `'tax_config.standard_rate'`). */
  errors?: Partial<Record<string, string>>;
  /** Disable all inputs (e.g. form submitting). */
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Common jurisdictions seeded for the dropdown. Full ISO-3166 catalogue is
// too long for a select — picker pre-populates the entities Printway ships
// today (HK / VN / US / SG / EU members) plus US states for SALES_TAX.
// Free-text edit not supported via dropdown — admins can type ISO in the
// "Other" option (handled by a hidden plain Input fallback).
// ---------------------------------------------------------------------------

const JURISDICTION_CODES: string[] = [
  'HK', 'VN', 'SG', 'CN', 'JP', 'KR', 'TH', 'MY', 'PH', 'ID', 'IN', 'AE',
  'AU', 'NZ', 'GB', 'DE', 'FR', 'NL', 'IT', 'ES', 'IE', 'US', 'US-CA',
  'US-NY', 'US-TX', 'US-WA', 'CA',
];

const EU_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR',
  'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO',
  'SE', 'SI', 'SK',
]);

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TaxConfigSection({
  value,
  onChange,
  addressCountry,
  errors = {},
  disabled = false,
}: TaxConfigSectionProps): JSX.Element {
  const { t } = useTranslation('finance');
  const disabledSubFields = value.type === 'NONE' || disabled;

  // Auto-suggest jurisdiction when address.country populated and current
  // jurisdiction is empty/default 'HK'. We only suggest — never overwrite a
  // jurisdiction the admin explicitly typed.
  useEffect(() => {
    if (value.type === 'NONE') return;
    const country = (addressCountry ?? '').trim().toUpperCase();
    if (!country) return;
    if (!value.jurisdiction || value.jurisdiction === 'HK') {
      if (country !== value.jurisdiction) {
        onChange('jurisdiction', country);
      }
    }
    // Auto-enable reverse-charge for EU B2B entities (default).
    if (EU_COUNTRIES.has(country) && !value.reverse_charge_eligible) {
      onChange('reverse_charge_eligible', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressCountry, value.type]);

  // Options list — ensure current jurisdiction value is always represented
  // even if it's something exotic (e.g. an older entity with 'AQ').
  const jurisdictionOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const code of JURISDICTION_CODES) {
      set.set(code, t(`legal_entity.tax.jurisdictions.${code}`));
    }
    if (value.jurisdiction && !set.has(value.jurisdiction)) {
      set.set(value.jurisdiction, value.jurisdiction);
    }
    return Array.from(set.entries()).map(([v, l]) => ({ value: v, label: l }));
  }, [value.jurisdiction, t]);

  const ratePercent = (value.standard_rate * 100).toFixed(2);

  return (
    <fieldset
      className="rounded-lg border border-pw-border p-4"
      data-testid="legal-entity-tax-config-section"
    >
      <legend className="px-2 text-sm font-semibold text-pw-text">
        {t('legal_entity.tax.legend')}
      </legend>

      {value.type === 'NONE' ? (
        <p className="mb-3 text-xs text-pw-text-muted">
          {t('legal_entity.tax.noneHint')}
        </p>
      ) : (
        <p className="mb-3 text-xs text-pw-text-muted">
          {t('legal_entity.tax.requiredHint')}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* --- Tax type --- */}
        <div>
          <label
            htmlFor="legal-entity-tax-type"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('legal_entity.tax.typeLabel')}
          </label>
          <Select
            value={value.type}
            onValueChange={(val) => onChange('type', val as TaxType)}
            disabled={disabled}
          >
            <SelectTrigger
              id="legal-entity-tax-type"
              data-testid="legal-entity-tax-type-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">
                {t('legal_entity.tax.typeNone')}
              </SelectItem>
              <SelectItem value="VAT">VAT</SelectItem>
              <SelectItem value="GST">GST</SelectItem>
              <SelectItem value="SALES_TAX">SALES_TAX</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* --- Standard rate (0–30%) --- */}
        <div>
          <label
            htmlFor="legal-entity-tax-rate"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('legal_entity.tax.rateLabel')}
            {value.type !== 'NONE' ? (
              <span className="ml-0.5 text-rose-600 dark:text-rose-300">*</span>
            ) : null}
          </label>
          <Input
            id="legal-entity-tax-rate"
            type="number"
            step="0.01"
            min={0}
            max={30}
            value={ratePercent}
            onChange={(e) => {
              const pct = Number(e.target.value);
              const decimal = Number.isFinite(pct) ? pct / 100 : 0;
              onChange('standard_rate', Math.max(0, Math.min(0.3, decimal)));
            }}
            placeholder={t('legal_entity.tax.ratePlaceholder')}
            disabled={disabledSubFields}
            aria-invalid={errors['tax_config.standard_rate'] ? 'true' : 'false'}
            data-testid="legal-entity-tax-rate-input"
          />
          {errors['tax_config.standard_rate'] ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {errors['tax_config.standard_rate']}
            </p>
          ) : (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('legal_entity.tax.rateHelp')}
            </p>
          )}
        </div>

        {/* --- Registration number --- */}
        <div>
          <label
            htmlFor="legal-entity-tax-registration"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('legal_entity.tax.regLabel')}
            {value.type !== 'NONE' ? (
              <span className="ml-0.5 text-rose-600 dark:text-rose-300">*</span>
            ) : null}
          </label>
          <Input
            id="legal-entity-tax-registration"
            value={value.registration_number ?? ''}
            onChange={(e) =>
              onChange(
                'registration_number',
                e.target.value === '' ? null : e.target.value,
              )
            }
            placeholder={t('legal_entity.tax.regPlaceholder')}
            disabled={disabledSubFields}
            aria-invalid={errors['tax_config.registration_number'] ? 'true' : 'false'}
            data-testid="legal-entity-tax-registration-input"
          />
          {errors['tax_config.registration_number'] ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {errors['tax_config.registration_number']}
            </p>
          ) : null}
        </div>

        {/* --- Jurisdiction --- */}
        <div>
          <label
            htmlFor="legal-entity-tax-jurisdiction"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('legal_entity.tax.jurisdictionLabel')}
          </label>
          <Select
            value={value.jurisdiction}
            onValueChange={(val) => onChange('jurisdiction', val)}
            disabled={disabledSubFields}
          >
            <SelectTrigger
              id="legal-entity-tax-jurisdiction"
              aria-invalid={errors['tax_config.jurisdiction'] ? 'true' : 'false'}
              data-testid="legal-entity-tax-jurisdiction-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {jurisdictionOptions.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors['tax_config.jurisdiction'] ? (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300" role="alert">
              {errors['tax_config.jurisdiction']}
            </p>
          ) : addressCountry ? (
            <p className="mt-1 text-xs text-pw-text-muted">
              {t('legal_entity.tax.jurisdictionAuto', {
                country: addressCountry.toUpperCase(),
              })}
            </p>
          ) : null}
        </div>

        {/* --- Invoice tax label --- */}
        <div>
          <label
            htmlFor="legal-entity-tax-label"
            className="mb-1 block text-sm font-medium text-pw-text"
          >
            {t('legal_entity.tax.invoiceLabelLabel')}
          </label>
          <Input
            id="legal-entity-tax-label"
            value={value.invoice_tax_label ?? ''}
            onChange={(e) =>
              onChange(
                'invoice_tax_label',
                e.target.value === '' ? null : e.target.value,
              )
            }
            placeholder={t('legal_entity.tax.invoiceLabelPlaceholder')}
            disabled={disabledSubFields}
            data-testid="legal-entity-tax-label-input"
          />
        </div>

        {/* --- Reverse-charge eligible --- */}
        <div className="flex items-center gap-3 pt-6">
          <input
            id="legal-entity-reverse-charge"
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-pw-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
            checked={value.reverse_charge_eligible}
            onChange={(e) => onChange('reverse_charge_eligible', e.target.checked)}
            disabled={disabledSubFields}
            data-testid="legal-entity-reverse-charge-checkbox"
          />
          <label
            htmlFor="legal-entity-reverse-charge"
            className="text-sm text-pw-text"
          >
            {t('legal_entity.tax.reverseChargeLabel')}{' '}
            <span className="text-xs text-pw-text-muted">
              {t('legal_entity.tax.reverseChargeHint')}
            </span>
          </label>
        </div>
      </div>
    </fieldset>
  );
}
